/**
 * @file inward-report.tsx
 * @module app/modules/inward/components/inward-report
 * @description End-of-day Inward report for the selected warehouse and day: preview table
 *   plus "Download Excel" and "Download PNG". Needs the Inward Download permission. Users with
 *   "Verify EFD receipt" (right 76) tick "EFD verified" per GRN in the EFD column.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import Loader from "../../../../Global/loader";
import { error } from "../../../../Global/toast";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import { canPerformInwardAction, InwardAction } from "../utils/inward-access";
import { eatToday } from "../utils/inward-po";
import InwardEfdCell from "./inward-efd-cell";
import {
  efdCellText,
  efdColumn,
  InwardReport as Report,
  InwardReportRow,
  reportRowCells,
  reportToPng,
  reportTotalCells,
  saveBlob,
} from "../utils/inward-report";
import InwardTabs from "./inward-tabs";
import { InwardNoAccess } from "./inward-no-access";
import "../styles/inward.css";

interface InwardReportProps {
  basePath: string;
}

const InwardReport = ({ basePath }: InwardReportProps) => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const canDownload = canPerformInwardAction(
    currentUser,
    InwardAction.DownloadReport
  );
  const [date, setDate] = useState(eatToday());
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    if (!canDownload || !date) return;
    (async () => {
      setLoading(true);
      const response = await new APICallService(
        INWARD.dailyReport,
        { date },
        "",
        "",
        "",
        "",
        PermissionModule.Inward,
        selectedWarehouse?._id
      ).callAPI();
      setReport(response?.rows ? response : null);
      setLoading(false);
    })();
  }, [canDownload, date, selectedWarehouse?._id]);

  if (!canDownload) return <InwardNoAccess />;

  // a tick saved: update that row (its EFD cell text too, so the PNG matches)
  const efdSaved = (
    grnRefKey: string,
    state: Pick<
      InwardReportRow,
      "efdVerified" | "efdVerifiedBy" | "efdVerifiedAt"
    >
  ) =>
    setReport((cur) => {
      if (!cur) return cur;
      const col = efdColumn(cur);
      return {
        ...cur,
        rows: cur.rows.map((r) => {
          if (r.grnRefKey !== grnRefKey) return r;
          const next = {
            ...r,
            ...state,
            efd: state.efdVerified
              ? "Verified"
              : r.efd === "Verified"
              ? "Pending"
              : r.efd,
          };
          const values = [...r.values];
          if (col >= 0) values[col] = efdCellText(next);
          return { ...next, values };
        }),
      };
    });

  const downloadExcel = async () => {
    setDownloading("excel");
    const response = await new APICallService(
      INWARD.dailyReportExcel,
      { date },
      "",
      "blob",
      "",
      "",
      PermissionModule.Inward,
      selectedWarehouse?._id
    ).callAPI();
    setDownloading("");
    if (!response) return;
    saveBlob(
      new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `inward-report-${date}.xlsx`
    );
  };

  const downloadPng = async () => {
    if (!report) return;
    setDownloading("png");
    const blob = await reportToPng(report);
    setDownloading("");
    if (!blob) {
      error("This browser cannot create the image");
      return;
    }
    saveBlob(blob, `inward-report-${date}.png`);
  };

  return (
    <div className="inward-page" data-testid="inward-report">
      <div className="inward-header">
        <div>
          <h1 className="fs-22 fw-bolder mb-1">Inward</h1>
          <p className="text-muted fs-14 mb-0">End-of-day inward report</p>
        </div>
      </div>
      <InwardTabs basePath={basePath} active="report" />

      <div className="inward-toolbar">
        <Form.Group className="inward-date-filter">
          <Form.Label className="fs-14 mb-1">Report date</Form.Label>
          <Form.Control
            type="date"
            className="inward-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="report-date"
          />
        </Form.Group>
        <div className="d-flex gap-2 flex-wrap align-self-end">
          <Button
            variant="success"
            className="inward-touch-btn"
            disabled={!report || downloading !== ""}
            onClick={downloadExcel}
            data-testid="report-excel"
          >
            {downloading === "excel" ? "Preparing…" : "Download Excel"}
          </Button>
          <Button
            className="inward-touch-btn"
            disabled={!report || downloading !== ""}
            onClick={downloadPng}
            data-testid="report-png"
          >
            {downloading === "png" ? "Preparing…" : "Download PNG"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center p-10">
          <Loader loading={loading} />
        </div>
      ) : !report ? (
        <div className="inward-empty" data-testid="report-empty">
          No report for this day.
        </div>
      ) : (
        <div className="table-responsive">
          {report.generatedAt && (
            <div
              className="fs-13 text-muted mb-2"
              data-testid="report-generated"
            >
              Pending times as of {report.generatedAt}
            </div>
          )}
          <Table
            bordered
            className="inward-report-table fs-14 mb-0"
            data-testid="report-table"
          >
            <thead>
              <tr>
                <th
                  colSpan={report.headers.length}
                  className="inward-report-title"
                >
                  {report.title}
                </th>
              </tr>
              <tr className="inward-report-header">
                {report.headers.map((h) => (
                  <th
                    key={h}
                    className={h.length > 20 ? "inward-report-wide" : ""}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={report.headers.length}
                    className="text-center text-muted"
                  >
                    No inward GRNs on this day.
                  </td>
                </tr>
              ) : (
                report.rows.map((r) => (
                  <tr key={r.grnRefKey} data-testid="report-row">
                    {reportRowCells(report, r).map((c, i) => (
                      <td
                        key={i}
                        className={`inward-report-cell ${
                          i < 10 && i !== 2 ? "text-center" : ""
                        }`}
                        data-testid={`report-cell-${i}`}
                      >
                        {report.canVerifyEfd && i === efdColumn(report) ? (
                          <InwardEfdCell
                            row={r}
                            warehouseId={selectedWarehouse?._id}
                            onSaved={(state) => efdSaved(r.grnRefKey, state)}
                          />
                        ) : (
                          c
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="inward-report-total" data-testid="report-total">
                {reportTotalCells(report).map((c, i) => (
                  <td key={i} className="text-center fw-bold">
                    {c}
                  </td>
                ))}
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
    </div>
  );
};

export default InwardReport;
